import { Box, Card, Grid } from "@mui/material";
import PropTypes from "prop-types";

import InspectionTimeline from "modules/inspections/components/InspectionTimeline";
import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";
import { Link, useNavigate, useParams } from "react-router-dom";
import SoftTypography from "components/SoftComponents/SoftTypography";
import { wrapper, deviceViewWrapper, loadingScreen } from "utils/wrapper";
import { useEffect, useState } from "react";
import InspectionPreviewCard from "modules/inspections/components/InspectionPreviewCard";

import inspectionAPI from "modules/inspections/api";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import moment from "moment";

function InspectionsThreadView({ map, user }) {
    inspectionAPI.loadToken(useAuth0());
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions, appearance } = controller;

    useEffect(() => {
        if (!map.current) return;
        disableMap(map, user, selectedOrg);
    }, [selectedOrg]);

    const navigate = useNavigate();
    const { inspection_record_id } = useParams();
    const [selectedInspection, setSelectedInspection] = useState(null);
    const [visitTypeFilter, setVisitTypeFilter] = useState("all");

    const viewUnresolved = userPermissions.includes("operations:unresolved");
    const showUnderReview = userPermissions.includes("operations:reviewer");

    const all_inspections = useQuery({
        queryKey: ['getAllInspectionsByRecordID', user, selectedOrg, inspection_record_id],
        queryFn: async () => {
            return await inspectionAPI.getAllInspectionsByRecordID(inspection_record_id);
        }
    });

    useEffect(() => {
        if (all_inspections.data && !all_inspections.isLoading && (!all_inspections.data.inspections?.length)) {
            navigate('/operations', { replace: true });
        }
    }, [all_inspections.data, all_inspections.isLoading, navigate]);

    const inspectionsData = all_inspections?.data?.inspections || [];

    // Initialize status filter once at the parent view level
    const [statusFilter, setStatusFilter] = useState(() => {
        if (viewUnresolved) {
            if (inspectionsData.some(row => row.status === "unresolved")) {
                return "unresolved";
            }
            else if (showUnderReview && inspectionsData.some(row => row.status === "review")) {
                return "review";
            }
        }

        return "all";
    });

    return wrapper((
        <SoftBox>
            {deviceLayout == "mobile" ? (
                <SoftBox borderRadius="20px" color={"white"} width="100%" height={'calc(100svh - 4.25rem - 8px)'} minHeight={"calc(100svh - 4.25rem - 8px)"}
                    sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
                >
                    {selectedInspection != null ? (
                        <Box m={1} sx={{ height: deviceLayout == 'mobile' ? 'calc(100% - 16px)' : '100%' }}>
                            <InspectionPreviewCard type={'inspection_thread'} {...{ user, selectedInspection, setSelectedInspection }} />
                        </Box>
                    ) : (
                        <SoftBox m={deviceLayout == 'mobile' ? 1 : 0} sx={{ overflowY: 'hidden', borderRadius: '10px', height: deviceLayout == 'mobile' ? 'calc(100% - 16px)' : "100%" }}>
                            <InspectionTimeline
                                type={'inspection_thread'}
                                isLoading={all_inspections.isLoading}
                                inspections_data={inspectionsData}
                                {...{ user, selectedInspection, setSelectedInspection, statusFilter, setStatusFilter, visitTypeFilter, setVisitTypeFilter }}
                            />
                        </SoftBox>
                    )}
                </SoftBox>

            ) : (
                <SoftBox borderRadius="20px" color={"white"} width="100%" height={"calc(100svh - 5.5rem - 16px)"}
                    sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
                >
                    <SoftBox m={1} sx={{ overflowY: 'hidden', borderRadius: '10px', height: 'calc(100% - 16px)' }}>
                        <InspectionTimeline
                            type={'inspection_thread'}
                            isLoading={all_inspections.isLoading}
                            inspections_data={inspectionsData}
                            {...{ user, selectedInspection, setSelectedInspection, statusFilter, setStatusFilter, visitTypeFilter, setVisitTypeFilter }}
                        >
                            <InspectionPreviewCard type={'inspection_thread'} {...{ user, selectedInspection, setSelectedInspection }} />
                        </InspectionTimeline>
                    </SoftBox>
                </SoftBox>
            )}
        </SoftBox>
    ), controller);
}

InspectionsThreadView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default InspectionsThreadView;